import { FadeInUp } from '@/components/shared/atoms/FadeInUp'
import { assetPath } from '@/utils/assetPath'
import { GlossyButton } from '@/components/shared/atoms/GlossyButton'
import { classNames } from '@/utils/classNames'
import { createFileRoute, useNavigate, Link, redirect } from '@tanstack/react-router'
import { useOnboardingStore, waitForOnboardingHydration } from '@/stores/onboarding'
import { ArrowLeft } from 'lucide-react'
import { useCallback, useState } from 'react'

const options = ['Womenswear', 'Menswear']

export const Route = createFileRoute(
    '/(onboarding)/(auth)/register/preferences'
)({
    validateSearch: (search) => ({
        returnToPath:
            typeof search.returnToPath === 'string'
                ? search.returnToPath
                : undefined,
        error:
            typeof search.error === 'string'
                ? search.error
                : undefined,
    }),
    beforeLoad: async () => {
        // Wait for store hydration from sessionStorage before checking state
        await waitForOnboardingHydration()
        const { registrationData } = useOnboardingStore.getState()
        if (!registrationData.username) {
            throw redirect({ to: '/register/username', search: { returnToPath: undefined, error: 'missing-username' } })
        }
    },
    component: RegisterPreferenceRoute,
})

function RegisterPreferenceRoute() {
    const navigate = useNavigate()
    const { returnToPath } = Route.useSearch()
    const { registrationData, setRegistrationData, markStepCompleted } =
        useOnboardingStore()
    // Initialize selected preference from store or default
    const [option, setOption] = useState(() => {
        const pref = registrationData.preference || ''
        const matched = options.find(
            (o) => o.toUpperCase() === pref.toUpperCase()
        )
        return matched || options[0]
    })

    const handleBack = useCallback(() => {
        navigate({ to: '/register/username', search: { returnToPath, error: undefined } })
    }, [navigate, returnToPath])

    const handleContinue = useCallback(() => {
        // Save preference in uppercase and proceed to account type
        setRegistrationData({ preference: option.toUpperCase() })
        markStepCompleted('preference')
        navigate({
            to: '/register/account-type',
            search: { returnToPath, error: undefined },
        })
    }, [option, setRegistrationData, markStepCompleted, navigate, returnToPath])

    return (
        <section className="relative z-10 flex min-h-dvh flex-col pt-safe">
            <div className="flex px-2 pt-3 md:justify-center">
                <img
                    alt="logo-red"
                    src={assetPath('logo/logo-red.svg')}
                    className="h-[34px] w-[155px] md:mt-12 md:h-[100px] md:w-[440px]"
                />
            </div>

            <FadeInUp delay={0.3}>
                <div className="px-4 pt-2 pb-6 md:pt-[35vh] md:text-center">
                    <h1 className="font-sfpro text-28 leading-8 font-semibold text-white">
                        Select your
                        <br className="md:hidden" />
                        <span className="text-white-48"> preference</span>
                    </h1>
                </div>
            </FadeInUp>

            <div className="flex flex-1 flex-col px-2">
                <FadeInUp delay={0.5}>
                    <div className="flex cursor-pointer items-center gap-2">
                        {options.map((item) => (
                            <GlossyButton
                                key={item}
                                variant="default"
                                className={classNames(
                                    'w-full cursor-pointer rounded-xl px-4 py-[14px] text-sm font-medium',
                                    item === option
                                        ? 'bg-white text-black'
                                        : 'bg-white/20 text-white backdrop-blur-md'
                                )}
                                onClick={() => setOption(item)}
                            >
                                {item}
                            </GlossyButton>
                        ))}
                    </div>
                </FadeInUp>

                <FadeInUp delay={0.7} className="mt-auto mb-6">
                    <div className="flex items-center gap-2 pt-6">
                        <GlossyButton
                            variant="ghost"
                            className="rounded-xl bg-white/20 px-4 py-[14px] text-sm font-medium text-white backdrop-blur-md"
                            onClick={handleBack}
                        >
                            <ArrowLeft
                                strokeWidth={1}
                                className="mr-1 h-5 w-5"
                            />
                            Back
                        </GlossyButton>
                        <GlossyButton
                            className="w-full rounded-xl py-[14px] text-sm"
                            onClick={handleContinue}
                        >
                            Continue
                        </GlossyButton>
                    </div>

                    <div className="mt-4 px-4 text-center">
                        <p className="text-sm font-normal text-white">
                            By creating an account, you agree that you have read
                            and accepted our{' '}
                            <Link
                                to="/terms-of-use"
                                className="font-semibold underline hover:text-white/80"
                            >
                                Terms of use.
                            </Link>
                        </p>
                    </div>
                </FadeInUp>
            </div>
        </section>
    )
}
